import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import {
  Check,
  Sparkles,
  Users,
  Zap,
  Crown,
  ChevronDown,
  Mail,
  ArrowRight,
  Star,
} from "lucide-react";

import "./Pricing.css";
import Navbar from "../Navbar/Navbar";
import Footer from "../LandingPage/Footer";

const LOGOKIT_TOKEN = process.env.REACT_APP_LOGOKIT_TOKEN;

const buildLogoUrl = (domain) => {
  // 1) Prefer Logokit (your original setup)
  if (LOGOKIT_TOKEN) return `https://img.logokit.com/${domain}?token=${LOGOKIT_TOKEN}`;
  // 2) Fallback to Clearbit (works without token)
  return `https://logo.clearbit.com/${domain}`;
};

const Pricing = () => {
  const navigate = useNavigate();

  const [billingCycle, setBillingCycle] = useState("annual"); // 'monthly' | 'annual'
  const [activeFaqIndex, setActiveFaqIndex] = useState(null);

  const handleGetStarted = () => navigate("/dashboard");
  const handleContactSales = () => navigate("/bookdemo");

  const pricingPlans = useMemo(
    () => [
      {
        name: "Starter",
        description: "For getting your first assistant live",
        priceAnnual: 11,
        priceMonthly: 14,
        icon: Sparkles,
        features: [
          "1 teammate",
          "1,000 conversations/month",
          "1 assistant",
          "10M knowledge characters",
          "Email support",
          "Basic analytics",
        ],
        highlighted: false,
        gradient: "from-blue-500 to-cyan-600",
      },
      {
        name: "Professional",
        description: "For growing teams and multiple assistants",
        priceAnnual: 30,
        priceMonthly: 37,
        icon: Users,
        features: [
          "2 teammates",
          "3,000 conversations/month",
          "2 assistants",
          "50M knowledge characters",
          "Priority support",
          "Advanced analytics",
          "API access",
          "Custom integrations",
        ],
        highlighted: true,
        badge: "Most Popular",
        gradient: "from-purple-500 to-purple-600",
      },
      {
        name: "Advanced",
        description: "For automation and heavier usage",
        priceAnnual: 179,
        priceMonthly: 224,
        icon: Zap,
        features: [
          "5 teammates",
          "12,000 conversations/month",
          "Unlimited assistants",
          "100M knowledge characters",
          "Premium support",
          "Advanced automation",
          "Workflow execution",
          "A/B testing",
        ],
        highlighted: false,
        gradient: "from-pink-500 to-rose-600",
      },
      {
        name: "Enterprise",
        description: "For security, scale, and custom requirements",
        priceAnnual: null,
        priceMonthly: null,
        icon: Crown,
        features: [
          "Custom teammates & limits",
          "Custom knowledge size",
          "Dedicated account manager",
          "Security & compliance",
          "SLA guarantees",
          "Custom integrations",
          "White-label options",
          "Advanced reporting",
        ],
        highlighted: false,
        isEnterprise: true,
        gradient: "from-indigo-500 to-purple-600",
      },
    ],
    []
  );

  const faqItems = useMemo(
    () => [
      {
        question: "What are the various pricing plans that XenaChat offers?",
        answer:
          "XenaChat offers Starter, Professional, Advanced, and Enterprise plans, tailored to different scales of businesses and integrations. Each plan is designed to grow with your needs.",
      },
      {
        question: "How can XenaChat's AI Agentic actions improve my business?",
        answer:
          "They automate customer interactions, reduce workload, improve sales conversions, and provide 24/7 support. Our AI agents can handle up to 70% of inquiries automatically.",
      },
      {
        question: "Do you have add-ons for extra usage or extra assistants?",
        answer:
          "Yes! You can add custom integrations, API access, extra team members, higher message quotas, and dedicated onboarding support as add-ons to any plan.",
      },
      {
        question: "Do you offer monthly billing and annual discounts?",
        answer:
          "Yes! Annual plans offer up to 20% discount compared to monthly rates. You can switch between monthly and annual billing anytime.",
      },
      {
        question: "What happens if I hit my conversation limit?",
        answer:
          "You can purchase message add-ons anytime via your dashboard. We'll also notify you before you reach your limit so you can upgrade or add more conversations.",
      },
      {
        question: "Is there a pay-as-you-go option?",
        answer:
          "Currently, only subscription plans are supported, but we are evaluating pay-as-you-go models based on customer feedback.",
      },
      {
        question: "How does the free trial work?",
        answer:
          "All paid plans include a 14-day free trial with full access to features. No credit card required. The monthly plans renew automatically unless you cancel before the trial ends.",
      },
      {
        question: "Which payment methods do you support?",
        answer:
          "We accept Visa, Mastercard, American Express, and PayPal. For Enterprise plans, we also support invoicing and wire transfers.",
      },
    ],
    []
  );

  const trustedLogos = useMemo(
    () => [
      { domain: "unwomen.org", alt: "UN Women" },
      { domain: "pepperstone.com", alt: "Pepperstone" },
      { domain: "nsf.gov", alt: "National Science Foundation" },
      { domain: "zooplus.com", alt: "Zooplus" },
      { domain: "admedia.com", alt: "AdMedia" },
      { domain: "next.co.uk", alt: "Next" },
      { domain: "deloitte.com", alt: "Deloitte" },
      { domain: "honeywell.com", alt: "Honeywell" },
      { domain: "coxmediagroup.com", alt: "Cox Media Group" },
      { domain: "ey.com", alt: "EY" },
      { domain: "benevity.com", alt: "Benevity" },
    ],
    []
  );

  const toggleFaq = (index) => {
    setActiveFaqIndex(activeFaqIndex === index ? null : index);
  };

  return (
    <>
      <Navbar />

      <div className="xc-pricing modern-pricing-page">
        {/* Hero */}
        <section className="pricing-hero">
          <div className="pricing-hero-container">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="pricing-hero-badge"
            >
              <Star className="badge-icon" />
              <span>Flexible Pricing</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="pricing-hero-title"
            >
              <span className="title-gradient">Plans That Scale</span>
              <br />
              With Your Conversations
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="pricing-hero-description"
            >
              Start small, go live fast, and upgrade as your team adds channels, agents, and automations.
            </motion.p>

            {/* Billing Toggle */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="billing-toggle"
            >
              <button
                className={`billing-option ${billingCycle === "monthly" ? "active" : ""}`}
                onClick={() => setBillingCycle("monthly")}
                type="button"
              >
                Monthly
              </button>
              <button
                className={`billing-option ${billingCycle === "annual" ? "active" : ""}`}
                onClick={() => setBillingCycle("annual")}
                type="button"
              >
                Annual <span className="savings-badge">Save 20%</span>
              </button>
            </motion.div>
          </div>
        </section>

        {/* Cards */}
        <section className="pricing-cards-section">
          <div className="pricing-cards-container">
            <div className="pricing-cards-grid">
              {pricingPlans.map((plan, index) => {
                const isAnnual = billingCycle === "annual";
                const shownPrice = plan.isEnterprise
                  ? null
                  : isAnnual
                  ? plan.priceAnnual
                  : plan.priceMonthly;

                const annualSave =
                  !plan.isEnterprise && plan.priceMonthly && plan.priceAnnual
                    ? (plan.priceMonthly - plan.priceAnnual) * 12
                    : 0;

                return (
                  <motion.div
                    key={plan.name}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className={`pricing-card ${plan.highlighted ? "highlighted" : ""} ${
                      plan.isEnterprise ? "enterprise" : ""
                    }`}
                  >
                    {plan.badge && (
                      <div className="popular-badge">
                        <Crown className="badge-icon" />
                        {plan.badge}
                      </div>
                    )}

                  <div className="card-header">
                    <div className="card-header-left">
                      <div className={`card-icon bg-gradient-to-br ${plan.gradient}`}>
                        <plan.icon className="icon" />
                      </div>

                      <div className="card-headings">
                        <h3 className="card-title">{plan.name}</h3>
                        <p className="card-description">{plan.description}</p>
                      </div>
                    </div>
                  </div>


                    <div className="card-pricing">
                      {!plan.isEnterprise ? (
                        <>
                          <div className="price-main">
                            <span className="currency">$</span>
                            <span className="amount">{shownPrice}</span>
                            <span className="period">/mo</span>
                          </div>

                          <div className="price-details">
                            {isAnnual ? (
                              <>Billed annually (${plan.priceAnnual * 12}/year)</>
                            ) : (
                              <>Billed monthly</>
                            )}
                          </div>

                          {!isAnnual && annualSave > 0 && (
                            <div className="annual-savings">
                              Save ${annualSave}/year with annual
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="price-main enterprise-price">Custom Pricing</div>
                      )}
                    </div>

                    <ul className="card-features">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="feature-item">
                          <div className="feature-check">
                            <Check className="check-icon" />
                          </div>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <button
                      className={`card-cta ${plan.highlighted ? "primary" : "secondary"}`}
                      onClick={plan.isEnterprise ? handleContactSales : handleGetStarted}
                      type="button"
                    >
                      {plan.isEnterprise ? (
                        <>
                          <span>Talk to Sales</span>
                          <ArrowRight className="btn-icon" />
                        </>
                      ) : (
                        <>
                          <Sparkles className="btn-icon" />
                          <span>Start Free Trial</span>
                        </>
                      )}
                    </button>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

    
        {/* FAQ */}
        <section className="pricing-faq-section">
          <div className="pricing-faq-container">
            <div className="faq-grid">
              {/* Left */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="faq-header"
              >
                <h2 className="faq-title">
                  Questions?
                  <br />
                  <span className="title-gradient">Here's what teams usually ask</span>
                </h2>

                <div
                  className="faq-contact-card"
                  role="button"
                  tabIndex={0}
                  onClick={handleContactSales}
                  onKeyDown={(e) => e.key === "Enter" && handleContactSales()}
                >
                  <div className="contact-icon">
                    <Mail className="icon" />
                  </div>
                  <div className="contact-content">
                    <div className="contact-title">Have other questions?</div>
                    <div className="contact-text">Contact us so we can help.</div>
                  </div>
                </div>
              </motion.div>

              {/* Right */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="faq-accordion"
              >
                {faqItems.map((item, index) => (
                  <div
                    key={index}
                    className={`faq-item ${activeFaqIndex === index ? "active" : ""}`}
                  >
                    <button
                      className="faq-question"
                      onClick={() => toggleFaq(index)}
                      type="button"
                    >
                      <span>{item.question}</span>
                      <motion.div
                        animate={{ rotate: activeFaqIndex === index ? 180 : 0 }}
                        transition={{ duration: 0.25 }}
                        className="faq-icon"
                      >
                        <ChevronDown className="icon" />
                      </motion.div>
                    </button>

                    <AnimatePresence>
                      {activeFaqIndex === index && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25 }}
                          className="faq-answer"
                        >
                          <p>{item.answer}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="pricing-cta-section">
          <div className="pricing-cta-container">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="cta-card"
            >
              <div className="cta-content">
                <h2 className="cta-title">
                  Need help choosing a <span className="title-gradient">plan?</span>
                </h2>
                <p className="cta-description">
                  Share your use case and channels, we'll recommend the right tier.
                </p>

                <div className="cta-buttons">
                  <button className="cta-btn primary" onClick={handleGetStarted} type="button">
                    <Sparkles className="btn-icon" />
                    <span>Get Started Free</span>
                  </button>
                  <button className="cta-btn secondary" onClick={handleContactSales} type="button">
                    <span>Contact Sales</span>
                    <ArrowRight className="btn-icon" />
                  </button>
                </div>

                <div className="cta-features">
                  <div className="cta-feature">
                    <Check className="feature-icon" />
                    <span>14-day free trial</span>
                  </div>
                  <div className="cta-feature">
                    <Check className="feature-icon" />
                    <span>No credit card required</span>
                  </div>
                  <div className="cta-feature">
                    <Check className="feature-icon" />
                    <span>Cancel anytime</span>
                  </div>
                </div>
              </div>

              <div className="cta-visual" aria-hidden="true">
                <div className="visual-orb orb-1"></div>
                <div className="visual-orb orb-2"></div>
                <div className="visual-orb orb-3"></div>
              </div>
            </motion.div>
          </div>
        </section>
      </div>

      <Footer />
    </>
  );
};

export default Pricing;
